"use client";

import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type ReactNode,
} from "react";
import { BrainIcon, ChevronDownIcon, Loader2 } from "lucide-react";

import { cn } from "~/lib/utils";

/**
 * Reasoning
 *
 * A collapsible block that surfaces a model's chain-of-thought (the streamed
 * `reasoning` message parts). It auto-expands while reasoning streams and
 * collapses once it finishes, showing how long the model thought — a lighter,
 * more honest replacement for a static "thinking…" placeholder.
 *
 * Mirrors the `ai-elements` Reasoning API (Reasoning / ReasoningTrigger /
 * ReasoningContent) but is built on a plain `useState` disclosure so it needs no
 * additional Collapsible/Radix dependency.
 */

type ReasoningContextValue = {
    isStreaming: boolean;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    /** Seconds the model spent reasoning, once streaming has finished. */
    durationSeconds: number | null;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

function useReasoning(): ReasoningContextValue {
    const context = useContext(ReasoningContext);
    if (!context) {
        throw new Error("Reasoning components must be used within <Reasoning>");
    }
    return context;
}

export type ReasoningProps = ComponentProps<"div"> & {
    /** True while the model is actively emitting reasoning tokens. */
    isStreaming?: boolean;
    /** Initial open state (ignored while streaming, which forces open). */
    defaultOpen?: boolean;
};

export function Reasoning({
    isStreaming = false,
    defaultOpen = false,
    className,
    children,
    ...props
}: ReasoningProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
    const startedAtRef = useRef<number | null>(null);
    const measuredRef = useRef(false);

    useEffect(() => {
        if (isStreaming) {
            // Reasoning began (or resumed): time it and open the disclosure.
            if (startedAtRef.current === null) {
                startedAtRef.current = Date.now();
            }
            measuredRef.current = false;
            setIsOpen(true);
            return;
        }
        // Reasoning finished: record how long it took, then collapse.
        if (startedAtRef.current !== null && !measuredRef.current) {
            measuredRef.current = true;
            setDurationSeconds(
                Math.max(
                    1,
                    Math.round((Date.now() - startedAtRef.current) / 1000),
                ),
            );
            setIsOpen(false);
        }
    }, [isStreaming]);

    return (
        <ReasoningContext.Provider
            value={{ isStreaming, isOpen, setIsOpen, durationSeconds }}
        >
            <div className={cn("flex flex-col", className)} {...props}>
                {children}
            </div>
        </ReasoningContext.Provider>
    );
}

export type ReasoningTriggerProps = ComponentProps<"button"> & {
    /** Override the label; receives the current streaming state + duration. */
    getLabel?: (
        isStreaming: boolean,
        durationSeconds: number | null,
    ) => ReactNode;
};

export function ReasoningTrigger({
    className,
    getLabel,
    ...props
}: ReasoningTriggerProps) {
    const { isStreaming, isOpen, setIsOpen, durationSeconds } = useReasoning();

    const defaultLabel = isStreaming
        ? "Thinking…"
        : durationSeconds
          ? `Thought for ${durationSeconds}s`
          : "Reasoning";
    const label = getLabel
        ? getLabel(isStreaming, durationSeconds)
        : defaultLabel;

    return (
        <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
                "flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                className,
            )}
            {...props}
        >
            {isStreaming ? (
                <Loader2
                    className="size-3.5 shrink-0 animate-spin text-primary"
                    aria-hidden="true"
                />
            ) : (
                <BrainIcon className="size-3.5 shrink-0" aria-hidden="true" />
            )}
            <span>{label}</span>
            <ChevronDownIcon
                className={cn(
                    "size-3.5 shrink-0 transition-transform duration-200",
                    isOpen && "rotate-180",
                )}
                aria-hidden="true"
            />
        </button>
    );
}

export type ReasoningContentProps = ComponentProps<"div"> & {
    children: ReactNode;
};

export function ReasoningContent({
    className,
    children,
    ...props
}: ReasoningContentProps) {
    const { isOpen } = useReasoning();
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className={cn(
                "mt-2 max-h-56 overflow-y-auto border-l-2 border-muted pl-3 text-xs leading-5 whitespace-pre-wrap text-tertiary-foreground",
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}
